import { useState, type FormEvent } from 'react'
import { ListFilter, Loader2, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { AudienceFilters } from '@/components/campaigns/AudienceFilters'
import { CampaignBasicsFields, type CampaignBasics } from '@/components/campaigns/CampaignBasicsFields'
import { CsvImport } from '@/components/campaigns/CsvImport'
import { LoadError } from '@/components/campaigns/LoadError'
import { DEFAULT_FILTERS, formatNumber, sumValues } from '@/components/campaigns/campaignFormat'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  useAudienceConsentQuery,
  useCampaignFilterOptionsQuery,
  usePreviewAudience,
  useUpdateCampaign,
} from '@/queries'
import type { AudienceConsent, CampaignFilters, CrmCampaign } from '@/services/crmCampaignsService'

const hasFilters = (filters: CampaignFilters | undefined) => Object.keys(filters ?? {}).length > 0

function ReasonList({ reasons, prefix }: { reasons: Record<string, number>; prefix: 'exclusion' | 'consent' }) {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const rows = Object.entries(reasons).filter(([, count]) => count > 0)
  if (rows.length === 0) return null
  return (
    <ul className="space-y-1 text-sm" data-testid={`${prefix}-reasons`}>
      {rows.map(([reason, count]) => (
        <li key={reason} className="flex items-baseline gap-2">
          <span className="font-medium tabular-nums">{formatNumber(count, lang)}</span>
          <span className="text-muted-foreground">
            {t(`campaigns.exclusion_reason.${reason}`, { defaultValue: reason })}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** La cifra que importa: a cuántos se enviará de verdad, una vez consultado el consentimiento. */
function ConsentProgress({ consent }: { consent: AudienceConsent }) {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  if (consent.total === 0) return null

  if (consent.estado !== 'hecho') {
    return (
      <div className="space-y-2" data-testid="consent-progress">
        <p className="text-sm">
          {t('campaigns.audience.consent_checking', {
            done: formatNumber(consent.comprobados, lang),
            total: formatNumber(consent.total, lang),
          })}
        </p>
        <Progress
          className="h-2"
          value={(consent.comprobados / consent.total) * 100}
          aria-label={t('campaigns.audience.consent_progress_label')}
        />
        <p className="text-xs text-muted-foreground">{t('campaigns.audience.consent_checking_help')}</p>
      </div>
    )
  }

  const discarded = sumValues(consent.descartados_por_motivo)
  return (
    <div className="space-y-2">
      <p className="text-sm" data-testid="consent-result">
        {t('campaigns.audience.consent_done', {
          sendable: formatNumber(consent.total - discarded, lang),
          discarded: formatNumber(discarded, lang),
        })}
      </p>
      <ReasonList reasons={consent.descartados_por_motivo} prefix="consent" />
    </div>
  )
}

/** Paso 1 del editor: de dónde sale la audiencia y cuánta gente queda. */
export function AudienceStep({ campaign }: { campaign: CrmCampaign }) {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const update = useUpdateCampaign(campaign.id)
  const preview = usePreviewAudience(campaign.id)
  const options = useCampaignFilterOptionsQuery()
  const consent = useAudienceConsentQuery(campaign.id)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [basics, setBasics] = useState<CampaignBasics>({
    nombre: campaign.nombre,
    audiencia_tipo: campaign.audiencia_tipo,
    excluir_agentes_excluidos: campaign.excluir_agentes_excluidos,
    parar_si_responde: campaign.parar_si_responde,
  })
  const [nameError, setNameError] = useState<string>()

  const basicsChanged =
    basics.nombre !== campaign.nombre ||
    basics.audiencia_tipo !== campaign.audiencia_tipo ||
    basics.excluir_agentes_excluidos !== campaign.excluir_agentes_excluidos ||
    basics.parar_si_responde !== campaign.parar_si_responde

  const saveBasics = (event: FormEvent) => {
    event.preventDefault()
    if (!basics.nombre.trim()) {
      setNameError(t('campaigns.editor.name_required'))
      return
    }
    setNameError(undefined)
    update.mutate({ ...basics, nombre: basics.nombre.trim() })
  }

  const applyFilters = async (filters: CampaignFilters) => {
    // Los filtros se guardan antes de calcular: la previsualización los lee de la campaña.
    await update.mutateAsync({ filtros: filters })
    await preview.mutateAsync()
    setSheetOpen(false)
  }

  const result = preview.data
  const filtersError = preview.error ?? (sheetOpen ? update.error : null)

  return (
    <div className="space-y-8">
      <form className="space-y-4" onSubmit={saveBasics} noValidate>
        <CampaignBasicsFields value={basics} nameError={nameError} onChange={setBasics} />
        {basicsChanged ? (
          <Button type="submit" variant="secondary" disabled={update.isPending}>
            {t('campaigns.editor.save_basics')}
          </Button>
        ) : null}
      </form>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">{t('campaigns.audience.title')}</h2>

        {campaign.audiencia_tipo === 'csv' ? (
          <CsvImport campaignId={campaign.id} />
        ) : options.isError ? (
          <LoadError error={options.error} onRetry={() => void options.refetch()} />
        ) : (
          <>
            <Button type="button" variant="outline" disabled={options.isPending} onClick={() => setSheetOpen(true)}>
              {options.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListFilter className="mr-2 h-4 w-4" />}
              {t('campaigns.audience.edit_filters')}
            </Button>
            <AudienceFilters
              open={sheetOpen}
              onOpenChange={setSheetOpen}
              applied={hasFilters(campaign.filtros) ? campaign.filtros : DEFAULT_FILTERS}
              options={options.data}
              knownCount={result?.total ?? null}
              applying={update.isPending || preview.isPending}
              onApply={(filters) => void applyFilters(filters).catch(() => undefined)}
            />
          </>
        )}

        {filtersError ? (
          <Alert variant="destructive">
            <AlertDescription>{filtersError.message}</AlertDescription>
          </Alert>
        ) : null}

        {result ? (
          <div className="space-y-2">
            <p className="flex flex-wrap items-baseline gap-2" data-testid="audience-count">
              <span className="text-2xl font-semibold tabular-nums">{formatNumber(result.incluidos, lang)}</span>
              <span className="text-sm text-muted-foreground">
                {t('campaigns.audience.included_of_total', { total: formatNumber(result.total, lang) })}
              </span>
            </p>
            <ReasonList reasons={result.excluidos_por_motivo} prefix="exclusion" />
            {result.sin_zona_dibujada > 0 ? (
              <p className="flex items-center gap-2 text-sm text-warning">
                <TriangleAlert className="h-4 w-4 shrink-0" />
                {t('campaigns.audience.no_drawn_zone', { count: formatNumber(result.sin_zona_dibujada, lang) })}
              </p>
            ) : null}
          </div>
        ) : null}

        {consent.isError ? (
          <LoadError error={consent.error} onRetry={() => void consent.refetch()} />
        ) : consent.data ? (
          <ConsentProgress consent={consent.data} />
        ) : null}
      </section>
    </div>
  )
}
